import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { MFOItem } from "@/types/kanban";

interface DeleteMFOConfirmationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  mfo: MFOItem | null;
  onConfirm: (mfoId: string) => Promise<void>;
}

export function DeleteMFOConfirmationDialog({
  isOpen,
  onOpenChange,
  mfo,
  onConfirm,
}: DeleteMFOConfirmationDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!mfo) return;
    
    setIsDeleting(true);
    try {
      await onConfirm(mfo.id);
    } finally {
      setIsDeleting(false);
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Confirm MFO Deletion
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this MFO?
            <div className="mt-2 p-3 bg-muted rounded-md">
              <p className="font-medium">{mfo?.code}: {mfo?.title}</p>
              <p className="text-sm text-muted-foreground mt-1">
                This action cannot be undone. This will permanently delete this MFO and all related data.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <Button 
            variant="destructive" 
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="gap-2"
          >
            {isDeleting ? "Deleting..." : "Delete MFO"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}